import React from 'react';
import type { AgentModelChoice } from '../../api/settings';
import { Badge } from '../ui';

/** One model the list can offer. */
export interface ModelOption extends AgentModelChoice {
  name: string;
  /** The price per picture, when the catalog has one. */
  price?: string;
}

/** A row: an option, and a note when the catalog does not list the model. */
type Row = ModelOption & { note?: string };

export type CatalogStatus = 'loading' | 'ready' | 'failed';

/** Rows shown at once: the list is searched, not scrolled through. */
const MAX_ROWS = 40;

const sameModel = (a: AgentModelChoice, b: AgentModelChoice) => a.provider === b.provider && a.model === b.model;

interface ModelMultiSelectProps {
  /** The group's name, and what one item of it is called ("image model"). */
  label: string;
  noun: string;
  catalog: ModelOption[];
  status: CatalogStatus;
  /** The models chosen; the locked one is not among them. */
  selected: AgentModelChoice[];
  onChange: (next: AgentModelChoice[]) => void;
  /** Always on: shown first, checked, disabled and marked "default". */
  locked?: AgentModelChoice;
  /** Extra marks for a row (async facts about the model). */
  marks?: (option: ModelOption) => React.ReactNode;
}

/**
 * A searchable multi-select over a model catalog. The chosen models and the
 * locked one stay in view whatever is searched; a chosen model the catalog does
 * not list (or a catalog that cannot be read) is still shown, by its id, so a
 * save never drops a choice the list failed to name.
 */
const ModelMultiSelect: React.FC<ModelMultiSelectProps> = ({ label, noun, catalog, status, selected, onChange, locked, marks }) => {
  const [query, setQuery] = React.useState('');
  const searchId = React.useId();

  const listed = (ref: AgentModelChoice) => catalog.find((option) => sameModel(option, ref));
  // A model the catalog does not name is shown by its id, and (once the catalog has answered) said not to be in it.
  const named = (ref: AgentModelChoice): Row =>
    listed(ref) ?? { ...ref, name: ref.model, ...(status === 'ready' ? { note: 'not in the current catalog' } : {}) };

  const chosen = selected.map(named);
  const needle = query.trim().toLowerCase();
  const matches = catalog.filter(
    (option) =>
      !selected.some((ref) => sameModel(ref, option)) &&
      !(locked && sameModel(locked, option)) &&
      (!needle || `${option.name} ${option.model} ${option.provider}`.toLowerCase().includes(needle))
  );
  const shown = matches.slice(0, MAX_ROWS);

  const toggle = (ref: AgentModelChoice) =>
    onChange(selected.some((s) => sameModel(s, ref)) ? selected.filter((s) => !sameModel(s, ref)) : [...selected, { provider: ref.provider, model: ref.model }]);

  const row = (option: Row, isLocked = false) => (
    <li key={`${option.provider}/${option.model}`}>
      <label className="flex min-h-10 cursor-pointer flex-wrap items-start gap-x-2 gap-y-1 rounded px-2 py-1.5 hover:bg-[var(--surface-3)]">
        <input
          type="checkbox"
          className="mt-1"
          checked={isLocked || selected.some((s) => sameModel(s, option))}
          disabled={isLocked}
          onChange={() => toggle(option)}
        />
        <span className="min-w-[10rem] flex-1">
          <span className="block break-words text-sm text-[var(--app-text)]">{option.name}</span>
          <span className="block break-all text-[11px] text-[var(--app-text-muted)]">
            {option.provider} · {option.model}
            {option.note && <span className="ml-1.5">({option.note})</span>}
          </span>
        </span>
        <span className="ml-auto flex flex-shrink-0 flex-wrap items-center justify-end gap-1.5 text-[11px] text-[var(--app-text-muted)]">
          {option.price && <span className="font-mono">{option.price}</span>}
          {marks?.(option)}
          {isLocked && <Badge variant="gray">default</Badge>}
        </span>
      </label>
    </li>
  );

  return (
    <fieldset className="min-w-0">
      <legend className="mb-1 text-[10px] uppercase tracking-widest text-[var(--app-text-muted)]">{label}</legend>
      <label htmlFor={searchId} className="sr-only">{`Search ${noun}s`}</label>
      <input
        id={searchId}
        type="search"
        className="input mb-1 w-full"
        placeholder={`Search ${noun}s`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {status === 'failed' && (
        <p className="mb-1 text-xs text-amber-400">{`The ${noun}s could not be listed. Your choices below still apply.`}</p>
      )}
      <ul className="max-h-64 overflow-y-auto rounded border border-[var(--border-subtle)] p-1">
        {locked && row(listed(locked) ?? { ...locked, name: locked.model }, true)}
        {chosen.filter((option) => !(locked && sameModel(locked, option))).map((option) => row(option))}
        {shown.map((option) => row(option))}
        {status === 'loading' && <li className="px-2 py-1.5 text-xs text-[var(--app-text-muted)]">Loading…</li>}
        {status === 'ready' && matches.length === 0 && needle && (
          <li className="px-2 py-1.5 text-xs text-[var(--app-text-muted)]">{`No ${noun} matches “${query.trim()}”.`}</li>
        )}
      </ul>
      {matches.length > shown.length && (
        <p className="mt-1 text-[11px] text-[var(--app-text-muted)]">{`Showing ${shown.length} of ${matches.length}. Search to narrow.`}</p>
      )}
    </fieldset>
  );
};

export default ModelMultiSelect;
